import { loadProfileOptions } from '../../services/profileOptionsService';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Stack, FormControl, InputLabel, Select, MenuItem,
  Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Avatar,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { supabase } from '../../supabaseClient.js';
import useAlert from '../../alert/useAlert.js';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';
import ClinicLocationManager from '../../components/ClinicLocationManager.js';
import { getTeamLocations } from '../../services/organizationService.js';
import { getProfileSectionStatus } from '../../utils/profileCompleteness.js';
import {
  LANGUAGES, DYNAMIC_OPTION_TYPES, SERVICE_FORMAT_OPTIONS,
} from '../../constants/profileOptions.js';
import OnboardingLayout from './OnboardingLayout.js';

const TEAL = '#29C0BB';

const SECTION_ICONS = {
  photo: <PersonOutlineIcon />,
  about: <PersonOutlineIcon />,
  languages: <LanguageIcon />,
  specialties: <FavoriteBorderIcon />,
  approaches: <SpaOutlinedIcon />,
  availability: <CalendarMonthOutlinedIcon />,
  fees: <PaidOutlinedIcon />,
};

/**
 * Step 5 — public-profile card grid. Each card opens a dialog editing the
 * fields it groups (per PROFILE_SECTIONS); saving a dialog persists just
 * those fields, so the card statuses always reflect what's in the DB.
 * The share-required fields the design doesn't surface (gender, phone,
 * contact email, address, assessment types, client groups, service formats)
 * live inside the closest matching card's dialog.
 */
const Step5PublicProfile = ({ ctx, onContinue, onSkip, onStepClick }) => {
  const { userId, initials, org } = ctx;
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [optionsByType, setOptionsByType] = useState({});

  // Editable profile fields (camelCase shape shared with profileCompleteness)
  const [profile, setProfile] = useState({
    profilePictureUrl: '',
    about: '',
    gender: '',
    phoneNumber: '',
    address: '',
    secondaryEmail: '',
    website: '',
    languages: [],
    preferredCaseTypes: [],
    assessmentTypes: [],
    clientGroups: [],
    interventionApproach: [],
    clinicLocations: [],
    serviceFormats: [],
    firstSessionDuration: '',
    firstSessionPrice: '',
    regularSessionDuration: '',
    regularSessionPrice: '',
    shareProfile: false,
    name: '',
  });

  const setField = (field, value) => setProfile((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('user_accounts').select('*').eq('id', userId).maybeSingle();
      if (data) {
        const [aboutRes, genderRes, phoneRes, addressRes, emailRes, websiteRes, firstPriceRes, regularPriceRes, nameRes] = await Promise.all([
          supabase.rpc('decrypt_text', { encrypted_data: data.about }),
          supabase.rpc('decrypt_text', { encrypted_data: data.gender }),
          supabase.rpc('decrypt_text', { encrypted_data: data.phone_number }),
          supabase.rpc('decrypt_text', { encrypted_data: data.address }),
          supabase.rpc('decrypt_text', { encrypted_data: data.secondary_email }),
          supabase.rpc('decrypt_text', { encrypted_data: data.website }),
          supabase.rpc('decrypt_text', { encrypted_data: data.first_session_price }),
          supabase.rpc('decrypt_text', { encrypted_data: data.regular_session_price }),
          supabase.rpc('decrypt_text', { encrypted_data: data.name }),
        ]);
        setProfile({
          profilePictureUrl: data.profile_picture_url || '',
          about: aboutRes.data || '',
          gender: genderRes.data || '',
          phoneNumber: phoneRes.data || '',
          address: addressRes.data || '',
          secondaryEmail: emailRes.data || '',
          website: websiteRes.data || '',
          languages: data.languages || [],
          preferredCaseTypes: data.preferred_case_types || [],
          assessmentTypes: data.assessment_types || [],
          clientGroups: data.client_groups || [],
          interventionApproach: data.intervention_approach || [],
          clinicLocations: data.clinic_locations || [],
          serviceFormats: data.service_formats || [],
          firstSessionDuration: data.first_session_duration || '',
          firstSessionPrice: firstPriceRes.data || '',
          regularSessionDuration: data.regular_session_duration || '',
          regularSessionPrice: regularPriceRes.data || '',
          shareProfile: Boolean(data.share_profile),
          name: nameRes.data || '',
        });
      }
      setLoading(false);
    };
    load();
  }, [userId]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setOptionsByType(await loadProfileOptions(userId));
      } catch (error) {
        // Existing selected labels remain in the draft if the catalog is unavailable.
        console.error('Could not load profile options:', error);
      }
    };
    loadOptions();
  }, [userId]);

  useEffect(() => {
    const loadLocations = async () => {
      if (!org?.id) return;
      const { data } = await getTeamLocations(org.id);
      setAvailableLocations((data || []).map((loc) => ({ ...loc, team_id: org.id, team_name: org.name })));
    };
    loadLocations();
  }, [org]);

  const sections = getProfileSectionStatus(profile);

  const saveFields = async (payload, sectionKey) => {
    setSaving(true);
    const { error } = await supabase.from('user_accounts').update(payload).eq('id', userId);
    setSaving(false);
    if (error) {
      setAlert('Unable to save. Please try again.', 'error');
      return;
    }
    setAlert('Saved.', 'success');
    setOpenSection(null);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAlert('Please upload an image file', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setAlert('Image size should be less than 10MB', 'error');
      return;
    }
    setUploadingImage(true);
    try {
      if (profile.profilePictureUrl) {
        const oldFileName = profile.profilePictureUrl.split('/').pop();
        await supabase.storage.from('profile-pictures').remove([`${userId}/${oldFileName}`]);
      }
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('profile-pictures').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('profile-pictures').getPublicUrl(filePath);
      const { error: dbError } = await supabase
        .from('user_accounts')
        .update({ profile_picture_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('id', userId);
      if (dbError) throw dbError;
      setField('profilePictureUrl', publicUrl);
      setAlert('Profile picture uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      setAlert('Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const renderStatus = (section) => {
    if (section.complete) {
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <CheckCircleIcon sx={{ fontSize: 18, color: TEAL }} />
          <Typography variant="body2" sx={{ color: TEAL, fontWeight: 600 }}>Added</Typography>
        </Stack>
      );
    }
    if (section.badge === 'required') {
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ErrorOutlineIcon sx={{ fontSize: 18, color: '#e5484d' }} />
          <Typography variant="body2" sx={{ color: '#e5484d', fontWeight: 600 }}>Not added</Typography>
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#8a94a0' }} />
        <Typography variant="body2" sx={{ color: '#8a94a0' }}>Not added</Typography>
      </Stack>
    );
  };

  if (loading) {
    return (
      <OnboardingLayout step={5} title="Build your public profile" avatarInitials={initials}>
        <CircularProgress sx={{ color: TEAL }} />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      step={5}
      title="Build your public profile"
      description="Help clients understand who you are, what you specialize in, and how you can help."
      sidebarExtra={
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <PublicOutlinedIcon sx={{ color: TEAL, fontSize: 20, mt: '2px' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Why this matters</Typography>
              <Typography variant="body2" sx={{ color: '#5b6770' }}>
                A complete profile helps clients find the right therapist and feel confident sending
                you a referral or booking.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <LockOutlinedIcon sx={{ color: TEAL, fontSize: 20, mt: '2px' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>You're in control</Typography>
              <Typography variant="body2" sx={{ color: '#5b6770' }}>
                Choose what information is visible on your public profile. You can edit or hide it anytime.
              </Typography>
            </Box>
          </Stack>
        </Stack>
      }
      avatarInitials={initials}
      onStepClick={onStepClick}
      onSkip={onSkip}
      skipCaption="You can complete this later"
      onContinue={onContinue}
      continueLabel="Save and continue"
      saving={saving}
    >
      {/* Card grid */}
      <Grid container spacing={2}>
        {sections.map((section) => (
          <Grid key={section.key} size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              onClick={() => setOpenSection(section.key)}
              sx={{
                border: '1px solid #e6eaee',
                borderRadius: 3,
                p: 2.5,
                height: '100%',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                transition: 'box-shadow 0.15s',
                '&:hover': { boxShadow: '0 2px 10px rgba(44,62,80,0.08)' },
              }}
            >
              <Avatar sx={{ bgcolor: '#DFF7F6', color: TEAL, mb: 1.5 }}>
                {SECTION_ICONS[section.key]}
              </Avatar>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{section.label}</Typography>
              <Chip
                label={section.badge === 'required' ? 'Required' : 'Recommended'}
                size="small"
                sx={{
                  mt: 0.75,
                  backgroundColor: section.badge === 'required' ? '#fdecec' : '#f0e9fb',
                  color: section.badge === 'required' ? '#e5484d' : '#8b5cf6',
                  fontWeight: 600,
                }}
              />
              <Typography variant="body2" sx={{ color: '#5b6770', mt: 1, flexGrow: 1 }}>
                {section.description}
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', mt: 1.5 }}>
                {renderStatus(section)}
                <ChevronRightIcon sx={{ color: '#8a94a0' }} />
              </Stack>
            </Box>
          </Grid>
        ))}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{ backgroundColor: '#f6f8f9', borderRadius: 3, p: 2.5, height: '100%' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}>About profile visibility</Typography>
            {[
              'Your profile is not visible to clients until you publish it.',
              'You can choose what information to show or hide.',
              'You can edit or unpublish your profile anytime.',
            ].map((line) => (
              <Stack key={line} direction="row" spacing={1} sx={{ mb: 1 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: TEAL, mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#5b6770' }}>{line}</Typography>
              </Stack>
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* ---- Section dialogs ---- */}

      {/* Profile photo */}
      <Dialog open={openSection === 'photo'} onClose={() => setOpenSection(null)} fullWidth maxWidth="xs">
        <DialogTitle>Profile photo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
            <Avatar src={profile.profilePictureUrl || undefined} sx={{ width: 100, height: 100 }} />
            <Button variant="outlined" component="label" disabled={uploadingImage}
              sx={{ textTransform: 'none', borderColor: TEAL, color: TEAL }}>
              {uploadingImage ? 'Uploading...' : 'Upload photo'}
              <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: TEAL }} onClick={() => setOpenSection(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* About me (+ identity/contact share-required fields) */}
      <Dialog open={openSection === 'about'} onClose={() => setOpenSection(null)} fullWidth maxWidth="sm">
        <DialogTitle>About me</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="About me *"
              multiline
              minRows={4}
              value={profile.about}
              onChange={(e) => {
                const value = e.target.value;
                // Same 200-character cap as Settings; shortening always allowed
                // so legacy over-length texts remain editable. Over-long input
                // (typically a paste) is clipped rather than dropped wholesale.
                if (value.length <= 200 || value.length < (profile.about || '').length) {
                  setField('about', value);
                } else {
                  setField('about', value.slice(0, 200));
                }
              }}
              helperText={`${(profile.about || '').length}/200 characters`}
              placeholder="Introduce yourself and your approach."
            />
            <FormControl fullWidth>
              <InputLabel>Gender *</InputLabel>
              <Select value={profile.gender} label="Gender *" onChange={(e) => setField('gender', e.target.value)}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Phone number *" value={profile.phoneNumber} onChange={(e) => setField('phoneNumber', e.target.value)} />
            <TextField label="Practice address *" value={profile.address} onChange={(e) => setField('address', e.target.value)} />
            <TextField label="Contact email *" type="email" value={profile.secondaryEmail} onChange={(e) => setField('secondaryEmail', e.target.value)} />
            <TextField label="Website" value={profile.website} onChange={(e) => setField('website', e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: '#666' }} onClick={() => setOpenSection(null)}>Cancel</Button>
          <Button
            sx={{ color: TEAL }}
            disabled={saving}
            onClick={() => saveFields({
              about: profile.about,
              gender: profile.gender,
              phone_number: profile.phoneNumber,
              address: profile.address,
              secondary_email: profile.secondaryEmail,
              website: profile.website,
            }, 'about')}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Languages */}
      <Dialog open={openSection === 'languages'} onClose={() => setOpenSection(null)} fullWidth maxWidth="sm">
        <DialogTitle>Languages</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <SearchableMultiSelect
              label="Languages *"
              options={LANGUAGES}
              value={profile.languages}
              onChange={(e) => setField('languages', e.target.value)}
              chipColor={TEAL}
              placeholder="Select the languages you provide therapy in..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: '#666' }} onClick={() => setOpenSection(null)}>Cancel</Button>
          <Button sx={{ color: TEAL }} disabled={saving} onClick={() => saveFields({ languages: profile.languages }, 'languages')}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Specialties (+ assessment types, client groups) */}
      <Dialog open={openSection === 'specialties'} onClose={() => setOpenSection(null)} fullWidth maxWidth="sm">
        <DialogTitle>Specialties</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableMultiSelect
              label="Speciality Areas *"
              options={optionsByType[DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES] || []}
              value={profile.preferredCaseTypes}
              onChange={(e) => setField('preferredCaseTypes', e.target.value)}
              chipColor={TEAL}
              placeholder="Search or select case types..."
            />
            <SearchableMultiSelect
              label="Assessment Types *"
              options={optionsByType[DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES] || []}
              value={profile.assessmentTypes}
              onChange={(e) => setField('assessmentTypes', e.target.value)}
              chipColor={TEAL}
              placeholder="Select assessment types..."
            />
            <SearchableMultiSelect
              label="Client Group *"
              options={optionsByType[DYNAMIC_OPTION_TYPES.CLIENT_GROUPS] || []}
              value={profile.clientGroups}
              onChange={(e) => setField('clientGroups', e.target.value)}
              chipColor={TEAL}
              placeholder="Select client group..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: '#666' }} onClick={() => setOpenSection(null)}>Cancel</Button>
          <Button
            sx={{ color: TEAL }}
            disabled={saving}
            onClick={() => saveFields({
              preferred_case_types: profile.preferredCaseTypes,
              assessment_types: profile.assessmentTypes,
              client_groups: profile.clientGroups,
            }, 'specialties')}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Therapy approaches */}
      <Dialog open={openSection === 'approaches'} onClose={() => setOpenSection(null)} fullWidth maxWidth="sm">
        <DialogTitle>Therapy approaches</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <SearchableMultiSelect
              label="Intervention Approach *"
              options={optionsByType[DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES] || []}
              value={profile.interventionApproach}
              onChange={(e) => setField('interventionApproach', e.target.value)}
              chipColor={TEAL}
              placeholder="Search or select approaches..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: '#666' }} onClick={() => setOpenSection(null)}>Cancel</Button>
          <Button sx={{ color: TEAL }} disabled={saving} onClick={() => saveFields({ intervention_approach: profile.interventionApproach }, 'approaches')}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Availability — per-location weekly hours on the locations from step 3 */}
      <Dialog open={openSection === 'availability'} onClose={() => setOpenSection(null)} fullWidth maxWidth="md">
        <DialogTitle>Availability</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#5b6770', mb: 2 }}>
            Set your available days and times for each clinic location you selected in step 3.
          </Typography>
          <ClinicLocationManager
            clinicLocations={profile.clinicLocations}
            onChange={(value) => setField('clinicLocations', value)}
            availableLocations={availableLocations}
          />
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: '#666' }} onClick={() => setOpenSection(null)}>Cancel</Button>
          <Button
            sx={{ color: TEAL }}
            disabled={saving}
            onClick={() => saveFields({ clinic_locations: profile.clinicLocations }, 'availability')}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session fees (+ service formats) */}
      <Dialog open={openSection === 'fees'} onClose={() => setOpenSection(null)} fullWidth maxWidth="sm">
        <DialogTitle>Session fees</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="First session duration (minutes) *"
                type="number"
                inputProps={{ min: 0, step: 5 }}
                value={profile.firstSessionDuration}
                onChange={(e) => setField('firstSessionDuration', e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
              <TextField
                fullWidth
                label="First session price (HKD) *"
                type="number"
                inputProps={{ min: 10, step: 1 }}
                value={profile.firstSessionPrice}
                onChange={(e) => setField('firstSessionPrice', e.target.value)}
                onWheel={(e) => e.target.blur()}
                error={Boolean(profile.firstSessionPrice !== '' && Number(profile.firstSessionPrice) < 10)}
                helperText={
                  (profile.firstSessionPrice !== '' && Number(profile.firstSessionPrice) < 10)
                    ? "Price must be at least 10 HKD."
                    : ""
                }
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Regular session duration (minutes) *"
                type="number"
                inputProps={{ min: 0, step: 5 }}
                value={profile.regularSessionDuration}
                onChange={(e) => setField('regularSessionDuration', e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
              <TextField
                fullWidth
                label="Regular session price (HKD) *"
                type="number"
                inputProps={{ min: 10, step: 1 }}
                value={profile.regularSessionPrice}
                onChange={(e) => setField('regularSessionPrice', e.target.value)}
                onWheel={(e) => e.target.blur()}
                error={Boolean(profile.regularSessionPrice !== '' && Number(profile.regularSessionPrice) < 10)}
                helperText={
                  (profile.regularSessionPrice !== '' && Number(profile.regularSessionPrice) < 10)
                    ? "Price must be at least 10 HKD."
                    : ""
                }
              />
            </Stack>
            <SearchableMultiSelect
              label="Service Format *"
              options={SERVICE_FORMAT_OPTIONS}
              value={profile.serviceFormats}
              onChange={(e) => setField('serviceFormats', e.target.value)}
              chipColor={TEAL}
              placeholder="Select service formats..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: '#666' }} onClick={() => setOpenSection(null)}>Cancel</Button>
          <Button
            sx={{ color: TEAL }}
            disabled={saving}
            onClick={() => {
              if (profile.firstSessionPrice !== '' && Number(profile.firstSessionPrice) < 10) {
                setAlert('First session price must be at least 10 HKD.', 'error');
                return;
              }
              if (profile.regularSessionPrice !== '' && Number(profile.regularSessionPrice) < 10) {
                setAlert('Regular session price must be at least 10 HKD.', 'error');
                return;
              }
              saveFields({
                first_session_duration: profile.firstSessionDuration,
                first_session_price: profile.firstSessionPrice,
                regular_session_duration: profile.regularSessionDuration,
                regular_session_price: profile.regularSessionPrice,
                service_formats: profile.serviceFormats,
              }, 'fees');
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

    </OnboardingLayout>
  );
};

export default Step5PublicProfile;
